// src/utils/designStore.js
// =====================  WHERE THIS GOES  =====================
// The bridge between the Design tab and the Assistant, mirroring
// sunPathStore/windStore: the panel that OWNS the data writes it, the
// assistant reads it at the moment a question is asked. Neither imports
// the other.
//
// A store rather than props because the two are siblings under
// DesignAnalysisPanel, and the alternative — lifting the whole parse,
// the scale and the traced-massing totals into that parent purely so a
// text box can read them — would put three components' state in a
// component that renders none of it.
// ============================================================

const state = {
  projectId: null, // whose design this is; see keyOf
  plan: null,      // { file, imageSize, scaleMpp, scaleSource, wallStandard, panels, rooms[] }
  massing: null,   // { floors, builtUpM2, groundCoverM2, totalHeightM, placement }
};

// Ids arrive as numbers from the project record and as strings from routes.
// Compared as strings so 110 and "110" are the same project, with null kept
// distinct from the string "null".
const keyOf = (id) => (id == null ? null : String(id));

/**
 * Adopt a project, discarding another project's design.
 *
 * The writers are inside the Design tab, which UNMOUNTS when the drawer shows
 * anything else; the assistant that reads this never unmounts. So there was no
 * moment at which a project switch could be observed here, and the previous
 * project's room schedule stayed readable — project B's design question,
 * answered from project A's rooms, with nothing on the answer saying so.
 */
function adopt(projectId) {
  const id = keyOf(projectId);
  if (state.projectId === id) return;
  state.projectId = id;
  state.plan = null;
  state.massing = null;
}

export const getDesignPlan = () => state.plan;
export const getDesignMassing = () => state.massing;

/**
 * The plan WITH its traced geometry, for a named project.
 *
 * readDesignContext() rounds the rooms down to names and sizes for the model,
 * which is right for a prompt and useless for anything that needs to know where
 * a room IS. The Vastu reading places each room on a grid, so it needs the pixel
 * polygons — but it must not get them for the wrong project, so this goes
 * through the same stamp check and fails closed the same way.
 */
export function getDesignPlanFor(projectId) {
  if (state.projectId !== keyOf(projectId)) return null;
  return state.plan;
}

export function setDesignPlan(p, projectId) { adopt(projectId); state.plan = p || null; }
export function setDesignMassing(m, projectId) { adopt(projectId); state.massing = m || null; }

/**
 * What the assistant is allowed to see about the design.
 *
 * Rounded on the way OUT, not on the way in: the panel keeps full precision for
 * its own arithmetic, and the model only ever needs the number a human would
 * read off the schedule. Sending 2.8749999 invites an answer quoting it.
 */
export function readDesignContext(projectId) {
  // Refuse a mismatch rather than trusting the writers to have cleared up. A
  // stale room schedule is worse than none: it is a confident answer about the
  // wrong building. Fails closed, so a panel that forgets to stamp its project
  // goes quiet instead of going wrong.
  if (state.projectId !== keyOf(projectId)) return null;
  const { plan, massing } = state;
  if (!plan && !massing) return null;
  return {
    plan: plan
      ? {
          file: plan.file || null,
          imageSize: plan.imageSize || null,
          scaleMpp: plan.scaleMpp != null ? +plan.scaleMpp.toFixed(5) : null,
          scaleSource: plan.scaleSource || null,
          wallStandard: plan.wallStandard || null,
          panels: plan.panels || [],
          roomCount: (plan.rooms || []).length,
          totalAreaM2: plan.totalAreaM2 != null ? +plan.totalAreaM2.toFixed(1) : null,
          rooms: (plan.rooms || []).map((r) => ({
            n: r.n,
            name: r.name || 'Unclassified',
            widthM: r.widthM != null ? +r.widthM.toFixed(2) : null,
            depthM: r.depthM != null ? +r.depthM.toFixed(2) : null,
            areaM2: r.areaM2 != null ? +r.areaM2.toFixed(1) : null,
            floor: r.floor ?? null,
          })),
        }
      : null,
    massing: massing || null,
  };
}

/**
 * Which plan the Design tab should open on, for one project.
 *
 * Set by the drawing intake when it sends a file, and read by PlanParseView the
 * next time it loads that project's plans. Without it the tab keeps whichever
 * plan it had selected — so a drawing just sent could arrive and not be the one
 * on screen. Cleared once PlanParseView has actually selected it, not on first
 * read, so a load that is superseded before it lands does not lose the hint.
 */
const preferred = { projectId: null, refId: null };

export function setPreferredPlan(projectId, refId) {
  preferred.projectId = keyOf(projectId);
  preferred.refId = refId || null;
}

export function peekPreferredPlan(projectId) {
  return preferred.refId && preferred.projectId === keyOf(projectId) ? preferred.refId : null;
}

export function clearPreferredPlan(refId) {
  if (!refId || preferred.refId === refId) {
    preferred.projectId = null;
    preferred.refId = null;
  }
}
